// src/components/map/utils/geojsonSource.ts
//
// Shared utility functions for managing GeoJSON sources/layers on a Mapbox map.
// Extracted as part of the adapter extraction plan (Phase 5) to eliminate
// duplication across adapter hooks. These are pure functions that take a
// mapbox instance as their first argument.
import type { GeoJSONSource, Map as MapboxMap } from "mapbox-gl";

import { emptyFeatureCollection } from "./emptyFeatureCollection";

/**
 * Clears a GeoJSON source by resetting its data to an empty FeatureCollection.
 * No-ops gracefully if the map instance is null or the layer does not exist.
 */
export function clearGeojsonSource(mapbox: Maybe<MapboxMap>, layer: string): void {
  if (!mapbox) return;
  if (mapbox.getLayer(layer)) {
    (mapbox.getSource(layer) as GeoJSONSource)?.setData({
      ...emptyFeatureCollection
    });
  }
}

/**
 * Sets data on an existing GeoJSON source if both the layer and source exist.
 * Returns `true` when the data was successfully applied, `false` otherwise
 * (e.g. the layer/source has not been added to the map yet).
 */
export function setGeojsonSource(
  mapbox: Maybe<MapboxMap>,
  layer: string,
  geojson: GeoJSON.FeatureCollection
): boolean {
  if (!mapbox) return false;
  if (mapbox.getLayer(layer) && mapbox.getSource(layer)) {
    const source = mapbox.getSource(layer) as GeoJSONSource;
    if (source) {
      source.setData(geojson);
      return true;
    }
  }
  return false;
}
